import { useQuery } from '@tanstack/react-query';

import type { GetMarkDeliveryFulfilledResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get delivery fulfilled status from a purchased vehicle
 */
export const getMarkDeliveryFulfilled = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetMarkDeliveryFulfilledResponse> => {
	const response = await apiClient.get<GetMarkDeliveryFulfilledResponse>(
		ordersEndpoints.markDeliveryFulfilled(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting delivery fulfilled status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetMarkDeliveryFulfilled = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.markDeliveryFulfilled.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get delivery fulfilled status without an order ID.');
			}
			return getMarkDeliveryFulfilled(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
